/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/layout/Footer.tsx
 *
 * Rôle :
 *   Pied de page permanent — bandeau partenaires (§13.5.4) + crédits auteur
 *   (§9.3) + liens institutionnels (mentions légales, RGPD, accessibilité,
 *   contact). Présent sur toutes les pages, sauf le splash et l'offline.
 *
 * Réutilisable :
 *   La structure (bandeau partenaires + crédits + liens institutionnels)
 *   est commune à toute app DRANE. Adapter la liste des liens.
 *
 * @author Mika
 * @since 2026-05-04
 */

import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { BandeauPartenaires } from './BandeauPartenaires';
import { ContentBlock } from '@/components/content/ContentBlock';
import { useSiteContent } from '@/hooks/useSiteContent';
import { useEventConfig } from '@/hooks/useEventConfig';
import { useEventIdentity } from '@/context/EventIdentityProvider';
import { formatEventWindowFr } from '@/utils/formatEventTime';
import styles from './Footer.module.css';

export function Footer() {
  // Coordonnées de l'événement (LOT-H — éditables au back-office).
  const { get } = useSiteContent();
  const { event } = useEventConfig();
  // Identité interpolée (LOT 3) — pour les replis i18n date/lieu + crédits.
  const identity = useEventIdentity();

  // Horaire affiché : OVERRIDE content.footer.event_time s'il est rempli,
  // SINON dérivé des bornes autoritaires `tne_events` (8 h 30 – 13 h 00).
  // La décision 12 h 00 vs 13 h 00 se règle donc côté donnée (jamais en dur).
  const eventTime =
    get('content.footer.event_time') ??
    formatEventWindowFr(event.starts_at, event.ends_at);

  return (
    <footer className={styles.footer} role="contentinfo">
      {/* Bandeau partenaires — §13.5.4 */}
      <BandeauPartenaires />

      {/* Coordonnées de l'événement (accroche + date · horaire · lieu) —
          contenu éditable /admin/content, repli i18n, horaire dérivé. */}
      <div className={styles.event}>
        <p className={styles.eventTagline}>
          <ContentBlock
            contentKey="content.footer.tagline"
            fallbackId="content.footer.tagline"
          />
        </p>
        <p className={styles.eventCoords}>
          <ContentBlock
            contentKey="content.footer.event_date"
            fallbackId="content.footer.event_date"
            values={identity}
          />
          {eventTime ? (
            <>
              {' · '}
              {eventTime}
            </>
          ) : null}
          {' · '}
          <ContentBlock
            contentKey="content.footer.event_place"
            fallbackId="content.footer.event_place"
            values={identity}
          />
        </p>
      </div>

      {/* Liens institutionnels */}
      <nav className={styles.legal} aria-label="Liens institutionnels">
        <ul>
          <li>
            <Link to="/a-propos">
              <FormattedMessage id="nav.about" />
            </Link>
          </li>
          <li>
            <Link to="/mentions-legales">
              <FormattedMessage id="nav.legal" />
            </Link>
          </li>
          <li>
            <Link to="/confidentialite">
              <FormattedMessage id="nav.privacy" />
            </Link>
          </li>
          <li>
            <Link to="/accessibilite">
              <FormattedMessage id="nav.accessibility" />
            </Link>
          </li>
          <li>
            <Link to="/contact">
              <FormattedMessage id="nav.contact" />
            </Link>
          </li>
        </ul>
      </nav>

      {/* Crédits permanents — §9.3 */}
      <div className={styles.credits}>
        <p>
          <FormattedMessage id="footer.organizer" values={identity} />
        </p>
        <p>
          <FormattedMessage id="footer.author" />
        </p>
        <p>
          <FormattedMessage id="footer.copyright" values={identity} />
        </p>
      </div>
    </footer>
  );
}
